import { registerLocaleData } from '@angular/common'
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http'
import localeDe from '@angular/common/locales/de'
import localeEn from '@angular/common/locales/en'
import {
  ApplicationConfig,
  enableProdMode,
  importProvidersFrom,
  LOCALE_ID,
} from '@angular/core'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import {
  provideRouter,
  RouteReuseStrategy,
  withHashLocation,
} from '@angular/router'
import { Capacitor } from '@capacitor/core'
import { IonicModule, IonicRouteStrategy } from '@ionic/angular'
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core'
import { TranslateHttpLoader } from '@ngx-translate/http-loader'
import { ApiModule, BASE_PATH, Configuration } from '@sciper/api-client'
import {
  AbstractSecurityStorage,
  LogLevel,
  OpenIdConfiguration,
  provideAuth,
  StsConfigHttpLoader,
  StsConfigLoader,
} from 'angular-auth-oidc-client'
import { provideMarkdown } from 'ngx-markdown'
import { map } from 'rxjs'

import { environment } from '../environments/environment'
import { ENVIRONMENT } from '../environments/environment.model'
import { routes } from './app.routes'
import { AppRoute } from './app.routes.fixtures'
import { AuthRefreshTokenInterceptor } from './interceptors/auth-refresh-token.interceptor'
import { AuthLocalStorageSecurityStorage } from './services/auth/auth.local-storage'
import { AuthService } from './services/auth/auth.service'
import { EnvironmentService } from './services/environment/environment.service'
import { DEFAULT_LANGUAGE } from './services/language/language.service'

if (environment.production) {
  enableProdMode()
}

registerLocaleData(localeDe)
registerLocaleData(localeEn)

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json')
}

export const openIdConfigLoaderFactory = (httpClient: HttpClient) => {
  const config$ = httpClient
    .get<any>(environment.oauthConfig.configurationUri)
    .pipe(
      map((config: any) => {
        const loginRedirect = Capacitor.isNativePlatform()
          ? `${environment.appScheme}${AppRoute.welcomeOrientation}?client_id=${environment.oauthConfig.clientId}`
          : environment.oauthConfig.redirectUri
        const logoutRedirect = Capacitor.isNativePlatform()
          ? `${environment.appScheme}${AppRoute.intro}?client_id=${environment.oauthConfig.clientId}`
          : environment.oauthConfig.postLogoutRedirectUri

        const configuration: OpenIdConfiguration = {
          configId: environment.configId,
          authority: config.issuer,
          clientId: environment.oauthConfig.clientId,
          responseType: environment.oauthConfig.responseType,
          scope: environment.oauthConfig.scope,
          redirectUrl: loginRedirect,
          postLogoutRedirectUri: logoutRedirect,
          triggerAuthorizationResultEvent: true,
          silentRenew: true,
          useRefreshToken: true,
          ignoreNonceAfterRefresh: true, // Keycloak sends refresh_token with nonce
          renewTimeBeforeTokenExpiresInSeconds: 10,
          authWellknownEndpointUrl: environment.oauthConfig.configurationUri,
          logLevel: environment.oauthConfig.showDebugInformation
            ? LogLevel.Debug
            : LogLevel.Warn,
        }
        return configuration
      })
    )
  return new StsConfigHttpLoader(config$)
}

export function apiConfigFactory(authService: AuthService): Configuration {
  return new Configuration({
    basePath: environment.apiBasePath + '/v1',
    credentials: {
      sciper_auth: authService.getAccessToken.bind(authService),
    },
  })
}

export function environmentProviderFactory(
  environmentService: EnvironmentService
): void {
  return environmentService.load()
}

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: ENVIRONMENT,
      useFactory: environmentProviderFactory,
      deps: [EnvironmentService, HttpClient],
    },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    importProvidersFrom(
      IonicModule.forRoot({}),
      BrowserAnimationsModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
        defaultLanguage: DEFAULT_LANGUAGE,
      })
    ),
    provideRouter(routes, withHashLocation()),
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthRefreshTokenInterceptor,
      multi: true,
    },
    provideAuth({
      loader: {
        provide: StsConfigLoader,
        useFactory: openIdConfigLoaderFactory,
        deps: [HttpClient, ENVIRONMENT],
      },
    }),
    {
      provide: BASE_PATH,
      useValue: environment.apiBasePath + '/v1',
    },
    {
      provide: Configuration,
      useFactory: apiConfigFactory,
      deps: [AuthService],
      multi: false,
    },
    {
      provide: ApiModule,
      deps: [HttpClient, Configuration],
    },
    {
      provide: AbstractSecurityStorage,
      useClass: AuthLocalStorageSecurityStorage,
    },
    {
      provide: LOCALE_ID,
      deps: [TranslateService],
      useFactory: (translateService: TranslateService) => {
        return translateService.getBrowserCultureLang()
      },
    },
    provideMarkdown(),
  ],
}
